import { Vector2 } from 'three';
/**
 * Tracks and updates the pointer state
 */
export class PointerState {
    constructor(canvas) {
        /**
         * Set of all unique pointers
         */
        Object.defineProperty(this, "pointers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * Is the Ctrl key pressed?
         */
        Object.defineProperty(this, "ctrlKey", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * Currently pressed pointer button
         */
        Object.defineProperty(this, "button", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * Current position of the hovering pointer
         */
        Object.defineProperty(this, "hoverPosition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * Previous position of the hovering pointer
         */
        Object.defineProperty(this, "prevHoverPosition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * Last removed pointer position
         */
        Object.defineProperty(this, "removedPosition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.pointers = new Map();
        this.ctrlKey = false;
        this.button = 0;
        this.hoverPosition = new Vector2();
        this.prevHoverPosition = new Vector2();
        this.removedPosition = new Vector2();
        // Mouse events
        canvas.addEventListener('mousedown', (ev) => {
            ev.preventDefault();
            canvas.focus();
            const { target, x, y, ctrlKey, button } = ev;
            const targetElement = target;
            const { left, top, width, height } = targetElement.getBoundingClientRect();
            this.addPointer(-1, Math.max(1, Math.min(width - 1, x - left)), Math.max(1, Math.min(height - 1, y - top)), ctrlKey, button);
        });
        canvas.addEventListener('mousemove', (ev) => {
            ev.preventDefault();
            const { target, x, y, ctrlKey } = ev;
            const targetElement = target;
            const { left, top, width, height } = targetElement.getBoundingClientRect();
            this.updatePointer(-1, Math.max(1, Math.min(width - 1, x - left)), Math.max(1, Math.min(height - 1, y - top)), ctrlKey);
        });
        canvas.addEventListener('mouseup', (ev) => {
            ev.preventDefault();
            const { target, x, y } = ev;
            const targetElement = target;
            const { left, top, width, height } = targetElement.getBoundingClientRect();
            this.removePointer(-1, Math.max(1, Math.min(width - 1, x - left)), Math.max(1, Math.min(height - 1, y - top)));
        });
        canvas.addEventListener('mouseleave', (ev) => {
            ev.preventDefault();
            const { target, x, y } = ev;
            const targetElement = target;
            const { left, top, width, height } = targetElement.getBoundingClientRect();
            this.removePointer(-1, Math.max(1, Math.min(width - 1, x - left)), Math.max(1, Math.min(height - 1, y - top)));
        });
        // Touch events
        canvas.addEventListener('touchstart', (ev) => {
            ev.preventDefault();
            canvas.focus();
            const { changedTouches, target, ctrlKey } = ev;
            const targetElement = target;
            const { left, top, width, height } = targetElement.getBoundingClientRect();
            for (const touch of changedTouches) {
                const { identifier, clientX, clientY } = touch;
                this.addPointer(identifier, Math.max(1, Math.min(width - 1, clientX - left)), Math.max(1, Math.min(height - 1, clientY - top)), ctrlKey, 0);
            }
        });
        canvas.addEventListener('touchmove', (ev) => {
            ev.preventDefault();
            const { changedTouches, target, ctrlKey } = ev;
            const targetElement = target;
            const { left, top, width, height } = targetElement.getBoundingClientRect();
            for (const touch of changedTouches) {
                const { identifier, clientX, clientY } = touch;
                this.updatePointer(identifier, Math.max(1, Math.min(width - 1, clientX - left)), Math.max(1, Math.min(height - 1, clientY - top)), ctrlKey);
            }
        });
        canvas.addEventListener('touchcancel', (ev) => {
            ev.preventDefault();
            const { changedTouches, target } = ev;
            const targetElement = target;
            const { left, top, width, height } = targetElement.getBoundingClientRect();
            for (const touch of changedTouches) {
                const { identifier, clientX, clientY } = touch;
                this.removePointer(identifier, Math.max(1, Math.min(width - 1, clientX - left)), Math.max(1, Math.min(height - 1, clientY - top)));
            }
        });
        canvas.addEventListener('touchend', (ev) => {
            ev.preventDefault();
            const { changedTouches, target } = ev;
            const targetElement = target;
            const { left, top, width, height } = targetElement.getBoundingClientRect();
            for (const touch of changedTouches) {
                const { identifier, clientX, clientY } = touch;
                this.removePointer(identifier, Math.max(1, Math.min(width - 1, clientX - left)), Math.max(1, Math.min(height - 1, clientY - top)));
            }
        });
    }
    /**
     * Add a new pointer
     *
     * @param id
     * @param x
     * @param y
     * @param ctrlKey
     * @param button
     */
    addPointer(id, x, y, ctrlKey, button) {
        this.pointers.set(id, {
            id,
            current: new Vector2(x, y),
            previous: new Vector2(x, y),
        });
        this.ctrlKey = ctrlKey;
        this.button = button;
    }
    /**
     * Update a pointer by id
     *
     * @param id
     * @param x
     * @param y
     * @param ctrlKey
     */
    updatePointer(id, x, y, ctrlKey) {
        const pointer = this.pointers.get(id);
        if (pointer) {
            pointer.previous.x = pointer.current.x;
            pointer.previous.y = pointer.current.y;
            pointer.current.x = x;
            pointer.current.y = y;
        }
        this.ctrlKey = ctrlKey;
        this.prevHoverPosition.copy(this.hoverPosition);
        this.hoverPosition.x = x;
        this.hoverPosition.y = y;
    }
    /**
     * Remove a pointer
     *
     * @param id
     * @param x
     * @param y
     */
    removePointer(id, x, y) {
        this.prevHoverPosition.copy(this.hoverPosition);
        this.hoverPosition.x = x;
        this.hoverPosition.y = y;
        this.removedPosition.x = x;
        this.removedPosition.y = y;
        this.pointers.delete(id);
        this.button = -1;
    }
    /**
     * Get the list of all active pointers
     *
     * @returns
     */
    getPointers() {
        return [...this.pointers.values()];
    }
    /**
     * Primarily applicable for mouse, get the current button pressed
     *
     * @returns
     */
    getButton() {
        return this.button;
    }
    /**
     * Test if the ctrl key is currently being pressed
     *
     * @returns
     */
    getCtrlKey() {
        return this.ctrlKey;
    }
    /**
     * Get the position of the last removed pointer
     *
     * @returns
     */
    getLastRemoved() {
        return this.removedPosition;
    }
    /**
     * Get the hover position of the pointer
     *
     * This position exists regardless of whether the pointer is pressed
     *
     * @returns
     */
    getHoverPosition() {
        return this.hoverPosition;
    }
    /**
     * Get the previous position of the hovering pointer
     *
     * @returns
     */
    getPreviousHoverPosition() {
        return this.prevHoverPosition;
    }
}
